import React from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../../shadcn/ui/dialog";

const EditUserRoleModal = ({
  isOpen,
  onClose,
  selectedUserForEdit,
  editUserRoleForm,
  editUserRoleLoading,
  editUserError,
  organizationRoles,
  onInputChange,
  onSubmit,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto bg-white rounded-lg border border-gray-200 p-0">
        <div className="p-6">
          <div className="mb-6">
            <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">Edit User Role</DialogTitle>
            <p className="text-sm text-gray-500">Update user role and permissions</p>
          </div>

          <div className="space-y-6 max-h-[60vh] overflow-y-auto">
            {editUserError && (
              <div className="p-3 bg-red-50 rounded-md border border-red-200">
                <p className="text-sm text-red-600">{editUserError}</p>
              </div>
            )}

            {editUserRoleLoading && !selectedUserForEdit && (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="mr-2 w-6 h-6 text-purple-600 animate-spin" />
                <span className="text-sm text-gray-600">Loading user data...</span>
              </div>
            )}

            {selectedUserForEdit && (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="font-medium text-gray-900">
                  {selectedUserForEdit.firstName} {selectedUserForEdit.lastName}
                </p>
                <p className="text-sm text-gray-500">{selectedUserForEdit.emailAddress}</p>
                <p className="mt-1 text-xs text-gray-500">
                  Current role: {selectedUserForEdit.organizationRole?.name || "No role assigned"}
                </p>
              </div>
            )}

            {selectedUserForEdit && (
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">New Role</label>
                <select
                  name="organizationRoleId"
                  value={editUserRoleForm.organizationRoleId}
                  onChange={onInputChange}
                  className="p-2 w-full rounded-md border border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                  disabled={editUserRoleLoading}
                >
                  <option value="">Select Role</option>
                  {(() => {
                    // Filter roles by organization type
                    const userOrgTypeId =
                      selectedUserForEdit.organization?.typeId || selectedUserForEdit.organization?.type?.id;

                    const filteredRoles = userOrgTypeId
                      ? organizationRoles.filter(role => role.organizationTypeId === userOrgTypeId)
                      : organizationRoles;

                    // If no roles found after filtering, show all roles as fallback
                    const rolesToShow = filteredRoles.length > 0 ? filteredRoles : organizationRoles;

                    return rolesToShow.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ));
                  })()}
                </select>
                {organizationRoles.length === 0 ? (
                  <p className="mt-1 text-xs text-gray-500">Loading available roles...</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    {(() => {
                      const userOrgTypeId =
                        selectedUserForEdit.organization?.typeId || selectedUserForEdit.organization?.type?.id;
                      return userOrgTypeId
                        ? `Showing roles for organization type ${userOrgTypeId}`
                        : "Showing all available roles";
                    })()}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={editUserRoleLoading || !editUserRoleForm.organizationRoleId}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {editUserRoleLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Role"
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserRoleModal;
